// frontend/src/auth.ts
import { createClient } from '@supabase/supabase-js';
import { authLogger, enableDebugLogging } from './utils/logger';

// Supabase configuration - using Vite environment variables
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://qnotjunsqpacbnbpkfin.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase configuration. Please set VITE_SUPABASE_ANON_KEY in your .env file.');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Debug Supabase client creation
authLogger.debug('Supabase client created', {
  url: SUPABASE_URL,
  hasKey: !!SUPABASE_ANON_KEY
});

// API base URL - adjust based on your backend
const isHosted =
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('railway.app') ||
    window.location.hostname.includes('lashingplan.com'));

export const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || (isHosted
  ? 'https://gallant-imagination-production2.up.railway.app' // Production backend
  : 'http://localhost:8001'); // Local dev

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  user?: any;
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (!data.session?.access_token) {
      throw new Error('No access token received');
    }

    // Store token in localStorage
    localStorage.setItem('jwt', data.session.access_token);
    if (data.session.refresh_token) {
      localStorage.setItem('refresh_token', data.session.refresh_token);
    }

    // --- Multi-Session Guard ---
    if (data.user && data.session) {
      const localSessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('active_session_id', localSessionId);
      
      const stampSession = async () => {
        try {
          if (data.session.access_token && data.session.refresh_token) {
            await supabase.auth.setSession({
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token
            });
          }
          const { error: updateError } = await supabase
            .from('user_profiles')
            .upsert({ id: data.user.id, email: data.user.email, active_session_id: localSessionId }, { onConflict: 'id' });
          if (updateError) authLogger.error('Failed to stamp session id', updateError);
        } catch (e) {
          authLogger.error('Error stamping active session id', e);
        }
      };
      stampSession();
    }
    // ---------------------------

    return {
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
      user: data.user,
    };
  } catch (err: any) {
    throw new Error(err.message || 'Login failed');
  }
}

/**
 * Get stored JWT token
 */
export function getJWT(): string | null {
  return localStorage.getItem('jwt');
}

/**
 * Get user details from stored JWT token
 */
export function getUser(): any | null {
  const token = getJWT();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch (e) {
    return null;
  }
}

export interface UserProfile {
  id: string;
  email: string;
  role: string;
  organization_id?: string | null;
}

/**
 * Fetch extended user profile featuring organization & role data
 */
export async function getUserProfile(): Promise<UserProfile | null> {
  const user = getUser();
  if (!user || !user.sub) return null;
  
  try {
    const { data: membershipData } = await supabase
      .from('memberships')
      .select('organization_id, role_id, roles(name)')
      .eq('user_id', user.sub)
      .limit(1)
      .single();

    return {
      id: user.sub,
      email: user.email,
      role: (membershipData?.roles as any)?.name || 'basic',
      organization_id: membershipData?.organization_id || null
    };
  } catch (e) {
     authLogger.warn('Failed to fetch full user profile: ', e);
     // Fallback to basic profiles query for legacy data before migration
     try {
         const { data: profileData } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', user.sub)
            .single();
         return {
            id: user.sub,
            email: user.email,
            role: profileData?.role || 'basic',
            organization_id: null
         };
     } catch (err) {
         return null;
     }
  }
}

/**
 * Remove stored tokens (logout)
 */
export function logout(): void {
  localStorage.removeItem('jwt');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('isSurveyor');
  supabase.auth.signOut();
}

// Enable debug logging only in development
if (import.meta.env.DEV) {
  enableDebugLogging();
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = getJWT();
  if (!token) {
    authLogger.debug('No token found');
    return false;
  }

  try {
    // Check if token is expired
    const payload = JSON.parse(atob(token.split('.')[1]));
    const isExpired = payload.exp * 1000 < Date.now();

    authLogger.debug('Token status', { isExpired, email: payload.email });

    if (!isExpired) return true;

    // Token is expired, try to refresh it
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      authLogger.debug('No refresh token found for expired JWT');
      return false;
    }

    authLogger.info('Attempting to refresh expired session');

    // Add a race with a timeout to prevent hanging the whole app
    const refreshPromise = supabase.auth.refreshSession({
      refresh_token: refreshToken
    });

    const timeoutPromise = new Promise<{ data: any, error: any }>((_, reject) => {
      setTimeout(() => reject(new Error('Supabase refresh session timeout')), 10000);
    });

    const { data, error } = await Promise.race([refreshPromise, timeoutPromise]);

    if (error) {
      // Don't log refresh token errors as errors - they're normal when sessions expire
      if (error.message?.includes('Refresh Token Not Found') ||
        error.message?.includes('Invalid Refresh Token')) {
        authLogger.info('Session expired - refresh token no longer valid');
      } else {
        authLogger.error('Error refreshing session', error);
      }
      logout();
      return false;
    }

    if (data.session) {
      authLogger.info('Session refreshed successfully');
      localStorage.setItem('jwt', data.session.access_token);
      if (data.session.refresh_token) {
        localStorage.setItem('refresh_token', data.session.refresh_token);
      }
      return true;
    }

    authLogger.warn('Refresh session returned no data');
    return false;
  } catch (e) {
    authLogger.error('Error checking authentication', e as Error);
    logout();
    return false;
  }
}

// Helper to create headers with auth token
export function createHeaders(token?: string | null, customHeaders: HeadersInit = {}): Headers {
  authLogger.debug('Creating headers', {
    hasToken: !!token,
    customHeaders: Object.keys(customHeaders)
  });

  const headers = new Headers(customHeaders);

  // Set default headers if not already set
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  // Add auth token if available
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  authLogger.debug('Headers created', {
    headerCount: Array.from(headers.keys()).length
  });
  return headers;
}

// Helper to handle fetch errors
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.text();
      try {
        errorData = JSON.parse(errorData);
      } catch {
        // Not JSON, keep as text
      }
    } catch (e) {
      errorData = response.statusText;
    }

    // Extract error message from different response formats
    let errorMessage = 'Request failed';
    if (typeof errorData === 'object' && errorData !== null) {
      // FastAPI returns errors in 'detail' field, which can be a string or an object
      const detail = errorData.detail;
      if (typeof detail === 'object' && detail !== null) {
        errorMessage = detail.message || detail.error_code || JSON.stringify(detail);
      } else {
        errorMessage = detail || errorData.message || errorMessage;
      }
    } else if (typeof errorData === 'string') {
      errorMessage = errorData;
    }

    const error = new Error(errorMessage);
    (error as any).response = response;
    (error as any).data = errorData;
    throw error;
  }

  // For 204 No Content responses
  if (response.status === 204) {
    return null as unknown as T;
  }

  return response.json();
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getJWT();
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  authLogger.debug('API request starting', {
    endpoint,
    hasToken: !!token,
    url
  });

  // Create headers with auth token
  const headers = createHeaders(token, options.headers);

  // Set content type for non-GET requests with body
  if (options.method && options.method.toUpperCase() !== 'GET' && options.body) {
    if (!headers.has('Content-Type') && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
  }

  // Configure fetch options
  const fetchOptions: RequestInit = {
    ...options,
    headers,
    mode: 'cors',
    cache: 'no-store',
  };

  // Handle credentials based on endpoint type
  if (endpoint === 'data' || endpoint === '/api/data' || endpoint.includes('/api/data')) {
    // For data endpoint, don't include credentials to avoid CORS issues
    fetchOptions.credentials = 'omit';
  } else if (token) {
    // For authenticated endpoints, include credentials
    fetchOptions.credentials = 'include';
  } else {
    // For non-authenticated endpoints, use same-origin
    fetchOptions.credentials = 'same-origin';
  }

  authLogger.debug('Fetch options configured', {
    mode: fetchOptions.mode,
    credentials: fetchOptions.credentials
  });

  try {
    authLogger.info('API request', {
      method: options.method || 'GET',
      url,
      mode: fetchOptions.mode
    });

    // Make the actual request
    const response = await fetch(url, fetchOptions);

    authLogger.info('API response received', {
      status: response.status,
      statusText: response.statusText,
      url
    });

    authLogger.debug('Response CORS headers', {
      origin: response.headers.get('Access-Control-Allow-Origin'),
      methods: response.headers.get('Access-Control-Allow-Methods'),
      headers: response.headers.get('Access-Control-Allow-Headers'),
      credentials: response.headers.get('Access-Control-Allow-Credentials')
    });

    // Handle 403 Forbidden - user needs to log in or has subscription issues
    if (response.status === 403) {
      let errorMessage = 'Özelliğe erişim izniniz yok. Lütfen aboneliğinizi kontrol edin.';
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch (e) {
        // Fallback to default if JSON parsing fails
      }
      authLogger.warn('403 Forbidden:', errorMessage);
      throw new Error(errorMessage);
    }

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        authLogger.warn('No refresh token available');
        logout();
        // Don't redirect automatically - let the app handle the authentication state
        throw new Error('Session expired. Please log in again.');
      }

      authLogger.info('Attempting to refresh session');
      try {
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: refreshToken,
        });

        if (error || !data.session) {
          throw error || new Error('No session data received');
        }

        // Update tokens
        authLogger.info('Session refreshed successfully');
        localStorage.setItem('jwt', data.session.access_token);
        if (data.session.refresh_token) {
          localStorage.setItem('refresh_token', data.session.refresh_token);
        }

        // Update authorization header and retry
        const newHeaders = new Headers(headers);
        newHeaders.set('Authorization', `Bearer ${data.session.access_token}`);

        authLogger.debug('Retrying original request with new token');
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers: newHeaders,
        });

        return handleResponse<T>(retryResponse);
      } catch (refreshError) {
        // Don't log refresh token errors as errors - they're normal when sessions expire
        const errorMessage = refreshError instanceof Error ? refreshError.message : String(refreshError);
        if (errorMessage?.includes('Refresh Token Not Found') ||
          errorMessage?.includes('Invalid Refresh Token')) {
          authLogger.info('Session expired - refresh token no longer valid');
        } else {
          authLogger.error('Failed to refresh session', refreshError as Error);
        }
        logout();
        // Don't redirect automatically - let the app handle the authentication state
        throw new Error('Session expired. Please log in again.');
      }
    }

    return handleResponse<T>(response);
  } catch (error) {
    authLogger.error('API request failed', {
      url,
      endpoint,
      error: error instanceof Error ? error.message : String(error)
    });


    // If it's a network error, provide a more helpful message
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      if (errorMessage.includes('failed to fetch') ||
        errorMessage.includes('load failed') ||
        errorMessage.includes('networkerror') ||
        errorMessage.includes('cors') ||
        errorMessage.includes('blocked')) {

        // Provide specific error messages based on endpoint
        if (endpoint === 'data' || endpoint.includes('/api/data')) {
          throw new Error('Unable to load application data. Please ensure the backend server is running and try again.');
        } else {
          throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
        }
      }

      // Handle timeout errors - show more user-friendly message
      if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        throw new Error('Hesaplama işlemi zaman aşımına uğradı. Lütfen tekrar deneyin veya veri miktarını azaltın.');
      }
    }

    throw error;
  }
}

/**
 * Helper for GET requests
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

/**
 * Helper for POST requests
 */
export async function apiPost<T>(
  endpoint: string,
  data?: any,
  options: RequestInit = {}
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    ...options,
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
  });
}

/**
 * Helper for PUT requests
 */
export async function apiPut<T>(
  endpoint: string,
  data?: any,
  options: RequestInit = {}
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    ...options,
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
  });
}

/**
 * Helper for DELETE requests
 */
export async function apiDelete<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
    ...options,
  });
}

/**
 * Simple check for super admin email
 */
export const checkSuperAdmin = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const SUPER_ADMIN_EMAIL = 'superadm@lashingplan.com';
  return email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
};
